import { Typography } from '@material-ui/core';
import { Site } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import React, { useMemo, useRef, useState } from 'react';
import Loadable from 'react-loadable';
import { useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import useJumpBackAndResetDeposit from '../../../../hooks/useJumpBackAndResetDeposit';
import type { HelpSection } from '../../../../types/Cashier';
import { CardType } from '../../../../types/cardType';
import { getPaySystemName } from '../../../../utils/cashier';
import CTAButton from '../../../CTAButton/CTAButton';
import CollapsiblePanel from '../../../CollapsiblePanel/CollapsiblePanel';
import Container from '../../../Container/Container';
import PaymentTitles from '../../../PaymentTitles/PaymentTitles';
import SuggestedPayment from '../../../Prompts/SuggestedPayment/SuggestedPayment';
import SavePaymentInfo from '../../../SavePaymentInfo/SavePaymentInfo';

import {
  TXTWCL_Deposit,
  TXTWCL_Deposit_methods_help,
  TXTWCL_How_is_my_data_kept_safeX,
  TXTWCL_Return_to_Cashier,
  TXTWCL_We_also_encrypt_your_data_so_i_ELL,
  TXTWCL_We_use_two_factor_authenticati_ELL,
  TXTWCL_Your_card_issuer_does_not_acce_ELL_2
} from './DepositDetails.messages.json';
import { depositDetails } from './DepositDetails.scss';
import HelpSectionContent from './components/HelpSectionContent/HelpSectionContent';
import SubmitButton from './components/SubmitButton/SubmitButton';
import TermsAndConditions from './components/TermsAndConditions/TermsAndConditions';

const DepositDetails: React.FC = () => {
  const {
    values: {
      deposit: { type }
    },
    data: { paySystems, paySystemDetails, savedCardInfoId }
  } = useWizardContext();

  const jumpBackAndResetDeposit = useJumpBackAndResetDeposit();
  const hostile = useRef(false);
  const [showHostilePrompt, setShowHostilePrompt] = useState(false);

  const showCardDetails = [
    CardType.Bancontact,
    CardType.CreditCard,
    CardType.DebitCard,
    CardType.GamersCard,
    CardType.MasterCard,
    CardType.Solo,
    CardType.Todito,
    CardType.Visa
  ].includes(type);

  const showSafeInfo = ![
    CardType.EnvoyLbt,
    CardType.PayPal,
    CardType.Resort,
    CardType.VanillaDirect,
    CardType.OpenBanking
  ].includes(type);
  const showSavePaymentInfo = ![CardType.PayPal, CardType.OpenBanking].includes(type);

  const systemName = getPaySystemName(paySystems, type);
  const site = useSelector(select.cms.getSiteId);

  const PaySystemDetails = useMemo(
    () =>
      Loadable({
        loader: async () => {
          // TODO: try dynamic import once https://github.com/webpack/webpack/issues/8656 is resolved
          if (savedCardInfoId) {
            return import('./SavedMethod/SavedMethod');
          }
          switch (type) {
            case CardType.Bancontact:
            case CardType.CreditCard:
            case CardType.DebitCard:
            case CardType.GamersCard:
            case CardType.MasterCard:
            case CardType.Solo:
            case CardType.Visa:
              return import('./Card/Card');
            case CardType.MBookers:
            case CardType.Skrill1Tap:
            case CardType.Pay4Fun:
            case CardType.Neteller2:
              return import('./components/PaymentEmail/PaymentEmail');
            case CardType.BoletoAstropay:
            case CardType.Pix:
            case CardType.PsPayInovapay:
              return import('./components/CPFPayments/CPFPayments');
            case CardType.ECheck:
              return import('./components/ECheck/ECheck');
            case CardType.EnvoyLbt:
              return import('./components/EnvoyLbt/EnvoyLbt');
            case CardType.MuchBetter:
              return import('./components/MuchBetter/MuchBetter');
            case CardType.Neosurf:
              return import('./components/Neosurf/Neosurf');
            case CardType.PStarsTopUp:
              return import('./components/PStarsTopUp/PStarsTopUp');
            case CardType.PsPayAstropay:
              return import('./components/PsPayAstropay/PsPayAstropay');
            case CardType.Ricarica:
              return import('./components/Ricarica/Ricarica');
            case CardType.Todito:
              return import('./components/Todito/Todito');
            case CardType.PayPal:
              return import('./components/PayPal/PayPal');
            case CardType.Resort:
              return import('./components/Resort/Resort');
            case CardType.VanillaDirect:
              return import('./components/VanillaDirect/VanillaDirect');
            case CardType.OpenBanking:
              return import('./components/OpenBanking/OpenBanking');
            default:
              return () => null;
          }
        },
        render: (loaded) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { default: Component } = loaded as any;
          return Component !== undefined ? (
            <>
              <fieldset>
                <Component systemName={systemName} cardType={type} hostile={hostile} />
                {showSavePaymentInfo && paySystemDetails.supportFastDeposit && (
                  <SavePaymentInfo type={type} />
                )}
                <TermsAndConditions type={type} />
                {(showCardDetails || showSafeInfo) && (
                  <CollapsiblePanel label={TXTWCL_How_is_my_data_kept_safeX} icon="info">
                    <Translation {...TXTWCL_We_use_two_factor_authenticati_ELL} />{' '}
                    <Translation {...TXTWCL_We_also_encrypt_your_data_so_i_ELL} />
                  </CollapsiblePanel>
                )}
              </fieldset>

              {CardType.Resort === type && (
                <CTAButton
                  variant="primary"
                  alias="firstWalletHome"
                  translation={TXTWCL_Return_to_Cashier}
                />
              )}
              {showSafeInfo && (
                <SubmitButton
                  hostile={hostile}
                  showHostilePrompt={() => setShowHostilePrompt(true)}
                />
              )}
            </>
          ) : (
            () => null
          );
        },
        loading: () => null
      }),
    [
      savedCardInfoId,
      type,
      systemName,
      showSavePaymentInfo,
      paySystemDetails.supportFastDeposit,
      showCardDetails,
      showSafeInfo,
      hostile
    ]
  );

  const helpSection: HelpSection = {
    title: TXTWCL_Deposit_methods_help,
    body: <HelpSectionContent />
  };

  return (
    <Container
      page={DepositDetails}
      pageClassName={depositDetails}
      headerProps={{ title: <Translation {...TXTWCL_Deposit} /> }}
      helpSupportResolver
      showRegulatoryInformation={type !== CardType.OpenBanking}
      showFooter
      limitsSection={Site.UK === site}
      helpSection={helpSection}
    >
      <PaymentTitles
        savedCardInfoId={savedCardInfoId}
        showSafeInfo={showSafeInfo}
        systemName={systemName}
        type={type}
      />
      <PaySystemDetails />
      {showHostilePrompt && (
        <SuggestedPayment
          onClose={() => setShowHostilePrompt(false)}
          cardType={type}
          paySystems={paySystems}
          onPrimaryButtonClick={() => jumpBackAndResetDeposit('#deposit.deposit-method')}
        >
          <Typography>
            <Translation {...TXTWCL_Your_card_issuer_does_not_acce_ELL_2} />
          </Typography>
        </SuggestedPayment>
      )}
    </Container>
  );
};

DepositDetails.displayName = 'DepositDetails';

export default DepositDetails;
